import { useEffect, useRef } from 'react';
import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { defaultKeymap } from '@codemirror/commands';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { yCollab, yUndoManagerKeymap } from 'y-codemirror.next';
import type { EditorMode } from '../store.js';
import { markdownLang, mdSyntaxHighlighting } from './markdown.js';
import { livePreview, refreshLivePreview } from './livePreview.js';
import { wikilinkAutocomplete } from './autocomplete.js';
import { editorKeymap, makeEditorCommands } from './commands.js';
import { imagePasteDrop } from './paste.js';
import { flashField, scrollToTarget } from './flash.js';
import { LinkResolver } from './resolver.js';
import { userColor } from './helpers.js';
import { ReadingView } from './reading.jsx';
import type { EditorCtx } from './context.js';
import './editor.css';

/**
 * NoteEditor renders a CodeMirror 6 editor bound to the note's Y.Text via
 * y-codemirror.next (collab-ready; pass awareness for remote cursors).
 *
 * Modes:
 *  - 'live': Obsidian-style live preview — markdown syntax hidden except
 *    around the cursor/selection; widget decorations for headings, bold,
 *    italic, links (wikilinks + md), checkboxes (toggleable), tables,
 *    image/audio/video embeds, note embeds ![[...]] including
 *    ![[note#heading]] and ![[note#^block]] transclusion.
 *  - 'source': plain markdown with syntax highlighting.
 *  - 'reading': rendered preview (react-markdown + remark-gfm + wikilinks),
 *    checkboxes clickable, links navigable.
 */
export interface NoteEditorProps {
  noteId: string;
  ytext: Y.Text;
  awareness?: Awareness;
  mode: EditorMode;
  onNavigate: (t: {
    id?: string;
    target?: string;
    heading?: string;
    blockId?: string;
  }) => void;
  scrollToHeading?: string;
  scrollToBlock?: string;
}

export function NoteEditor(props: NoteEditorProps) {
  if (props.mode === 'reading') {
    return <ReadingView key={props.noteId} {...props} />;
  }
  return <CmEditor key={props.noteId} {...props} />;
}

function CmEditor(props: NoteEditorProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const modeCompRef = useRef<Compartment | null>(null);
  const ctxRef = useRef<EditorCtx | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;

  // Create the view (recreated when the note changes).
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const { noteId, ytext, awareness } = propsRef.current;

    const resolver = new LinkResolver(noteId, () => {
      viewRef.current?.dispatch({ effects: refreshLivePreview.of(null) });
    });
    const ctx: EditorCtx = {
      noteId,
      navigate: (t) => propsRef.current.onNavigate(t),
      resolve: (target) => resolver.resolve(target),
    };
    ctxRef.current = ctx;

    if (awareness) {
      const st = awareness.getLocalState() as { user?: { name?: string } } | null;
      const c = userColor(awareness.clientID);
      awareness.setLocalStateField('user', {
        name: st?.user?.name ?? 'me',
        color: c.color,
        colorLight: c.light,
      });
    }

    // CodeMirror collapses CRLF to LF at state creation while y-codemirror
    // maps offsets 1:1 onto the Y.Text; a CRLF-backed doc would desync and
    // corrupt on edit. The server normalizes on import, but guard here too
    // for any doc persisted before that (one-time, merges cleanly).
    if (ytext.toString().includes('\r')) {
      const normalized = ytext.toString().replace(/\r\n?/g, '\n');
      (ytext.doc ?? new Y.Doc()).transact(() => {
        ytext.delete(0, ytext.length);
        ytext.insert(0, normalized);
      });
    }

    const undoManager = new Y.UndoManager(ytext);
    const modeComp = new Compartment();
    modeCompRef.current = modeComp;

    const state = EditorState.create({
      doc: ytext.toString(),
      extensions: [
        keymap.of([...yUndoManagerKeymap]),
        keymap.of(editorKeymap),
        markdownLang(), // includes list-continuation Enter/Backspace keymap
        keymap.of(defaultKeymap),
        keymap.of(searchKeymap),
        mdSyntaxHighlighting,
        EditorView.lineWrapping,
        EditorView.contentAttributes.of({
          autocapitalize: 'sentences',
          autocorrect: 'on',
          spellcheck: 'true',
        }),
        wikilinkAutocomplete(ctx),
        highlightSelectionMatches(),
        yCollab(ytext, awareness ?? null, { undoManager }),
        modeComp.of(propsRef.current.mode === 'live' ? livePreview(ctx) : []),
        imagePasteDrop(),
        flashField,
      ],
    });

    const view = new EditorView({ state, parent: host });
    viewRef.current = view;
    registerEditorCommands(makeEditorCommands(view, undoManager));

    return () => {
      registerEditorCommands(null);
      resolver.dispose();
      viewRef.current = null;
      modeCompRef.current = null;
      ctxRef.current = null;
      view.destroy();
      undoManager.destroy();
    };
  }, [props.noteId, props.ytext]);

  // Cheap live <-> source switches via the compartment.
  useEffect(() => {
    const view = viewRef.current;
    const comp = modeCompRef.current;
    const ctx = ctxRef.current;
    if (!view || !comp || !ctx) return;
    view.dispatch({
      effects: comp.reconfigure(props.mode === 'live' ? livePreview(ctx) : []),
    });
  }, [props.mode]);

  // Scroll to heading/block on mount or prop change.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const { scrollToHeading, scrollToBlock } = props;
    if (scrollToHeading || scrollToBlock) {
      scrollToTarget(view, { heading: scrollToHeading, blockId: scrollToBlock });
    }
  }, [props.noteId, props.scrollToHeading, props.scrollToBlock]);

  return <div className="note-editor" ref={hostRef} />;
}

/** Imperative editor commands the shortcut row / shell may invoke. */
export interface EditorCommands {
  insertText(text: string, cursorOffset?: number): void;
  toggleCheckbox(): void;
  wrapSelection(before: string, after: string): void;
  undo(): void;
  redo(): void;
}

let activeCommands: EditorCommands | null = null;
export const registerEditorCommands = (c: EditorCommands | null) => {
  activeCommands = c;
};
export const editorCommands = () => activeCommands;
